import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { queryClient } from "@/trpc/client";
import { stripProxyHostParamFromAddressBar } from "./lib/blog/proxyHostAddressBar";
import { routeTree } from "./routeTree.gen";

export function getRouter() {
  // #544: drop the standalone proxy's `__gshost` cache-key param from the address bar
  // before the router reads the location, so it never shows to a user or seeds a
  // client-side navigation. No-op on the server and whenever the param is absent.
  stripProxyHostParamFromAddressBar();

  const router = createTanStackRouter({
    routeTree,
    // Seed the "auth not resolved yet" state so auth-gated `beforeLoad`
    // guards (e.g. src/routes/dashboard.tsx) don't redirect signed-in users
    // during SSR / the initial client route. Mirrors `authStore`'s initial
    // `isLoading: true`; `AuthAndSentryBootstrap` pushes the real state in
    // once `initAuth()` resolves.
    context: { auth: { user: null, isLoading: true } },
    // D-216-5 (selective SSR): routes default to client-only via
    // `defaultSsr: false` in src/start.ts (the Start instance owns that
    // option — the router constructor doesn't accept it).
    scrollRestoration: true,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
  });

  // 216b (#219): dehydrate react-query state on the server and hydrate it on
  // the client, so loaders/queries on SSR'd routes (the blog, #200) render
  // once instead of refetching after hydration. Uses the app-wide QueryClient:
  // no server-side query runs today (authed routes are `ssr: false` per
  // D-216-5, public routes fetch nothing until #200), so per-request client
  // isolation is deferred to #200 where server queries first appear —
  // see specs/219-ssr-data-hydration/DECISIONS.md.
  setupRouterSsrQueryIntegration({ router, queryClient });

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
